import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "./_shared";
import { Input } from "@/components/ui/input";
import { Archive, Search, ShieldCheck, FileText, Briefcase, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";

export default function ArchivedSSSPs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async (search) => {
    setLoading(true);
    try {
      const r = await api.get("/sssps/archived", { params: search ? { q: search } : {} });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Could not load archived SSSPs");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const viewUploaded = async (id) => {
    try {
      const r = await api.get(`/job-sssp/docs/${id}/record.pdf?download=0`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Could not open document");
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—";

  const empty = useMemo(() => !loading && items.length === 0, [loading, items]);

  return (
    <div data-testid="archived-sssps-page" className="p-6 lg:p-10 max-w-6xl mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ Health & Safety"
        title="Archived SSSPs"
        subtitle="Safety plans that were tidied away when their job was archived. Restore the job to bring its SSSPs back."
      />

      <div className="flex items-start gap-2 text-xs text-zinc-500 mb-4 bg-zinc-50 dark:bg-white/[0.03] border border-zinc-200 dark:border-white/10 rounded-lg px-3 py-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>SSSPs archive automatically with their job and are read-only here. Restoring the job from Archived Jobs brings these plans back to the live register.</span>
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plans, jobs, authors…"
               className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
               data-testid="archived-sssp-search-input" />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm p-6">Loading…</div>
      ) : empty ? (
        <EmptyState
          icon={Archive}
          title={q.trim() ? "No matches" : "No archived SSSPs"}
          subtitle={q.trim() ? "Try a different search." : "When you archive a completed job, its SSSPs will appear here."}
        />
      ) : (
        <div className="space-y-2" data-testid="archived-sssp-list">
          {items.map((m) => (
            <div key={`${m.kind}-${m.id}`}
                 className="hard-card flex items-center gap-4 px-4 py-3"
                 data-testid={`archived-sssp-${m.id}`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.kind === "built" ? "bg-red-500/10 text-red-500" : "bg-[#FF5F15]/10 text-[#FF5F15]"}`}>
                {m.kind === "built" ? <ShieldCheck className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold uppercase tracking-tight truncate flex items-center gap-2">
                  {m.name || "Untitled SSSP"}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-zinc-100 dark:bg-white/10 border border-zinc-300 dark:border-white/10 rounded-full">
                    {m.kind === "built" ? "Built" : "Uploaded"}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 truncate flex items-center gap-1.5">
                  {m.job_title ? <><Briefcase className="w-3 h-3" /> {m.job_number ? `${m.job_number} · ` : ""}{m.job_title} · </> : null}
                  {m.author ? `By ${m.author} · ` : ""}Archived {fmt(m.archived_at)}
                </div>
              </div>
              {m.kind === "built" ? (
                <Link to={`/app/health-safety/sssps/${m.id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-[#FF5F15] shrink-0"
                      data-testid={`archived-sssp-view-${m.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> View
                </Link>
              ) : (
                <button onClick={() => viewUploaded(m.id)}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-[#FF5F15] shrink-0"
                        data-testid={`archived-sssp-view-${m.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> View
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
